import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Play, Check, Lock, X, Eye, Calculator } from 'lucide-react';
import { payrollRunService, payCycleService, payslipService } from '../api/mockRepo';
import { staffService } from '../../staff/api/mockRepo';
import type { PayrollRun, PayCycle, PayrollRunFormData } from '../types';
import type { Staff } from '../../staff/types';
import toast from 'react-hot-toast';

const payrollRunSchema = z.object({
  cycle_id: z.string().min(1, 'Pay cycle is required'),
  period_start: z.string().min(1, 'Start date is required'),
  period_end: z.string().min(1, 'End date is required'),
  notes: z.string().optional()
});

export default function PayrollRunsPage() {
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [cycles, setCycles] = useState<PayCycle[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [processingRun, setProcessingRun] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<PayrollRunFormData>({
    resolver: zodResolver(payrollRunSchema)
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [runsData, cyclesData, staffData] = await Promise.all([
        payrollRunService.getAll(),
        payCycleService.getAll(),
        staffService.getAll()
      ]);
      setRuns(runsData);
      setCycles(cyclesData);
      setStaff(staffData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load payroll runs');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: PayrollRunFormData) => {
    try {
      const created = await payrollRunService.create(data);
      setRuns(prev => [created, ...prev]);
      toast.success('Payroll run created successfully');
      setShowModal(false);
      reset();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create payroll run');
    }
  };

  const handleCalculatePayroll = async (runId: string) => {
    setProcessingRun(runId);
    try {
      await payslipService.generatePayslips(runId, staff);
      const updatedRuns = await payrollRunService.getAll();
      setRuns(updatedRuns);
      toast.success('Payroll calculated successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to calculate payroll');
    } finally {
      setProcessingRun(null);
    }
  };

  const handleApproveRun = async (runId: string) => {
    try {
      await payrollRunService.updateStatus(runId, 'approved', 'current-user');
      const updatedRuns = await payrollRunService.getAll();
      setRuns(updatedRuns);
      toast.success('Payroll run approved');
    } catch (error: any) {
      toast.error(error.message || 'Failed to approve payroll run');
    }
  };

  const handleLockRun = async (runId: string) => {
    try {
      await payrollRunService.updateStatus(runId, 'locked');
      const updatedRuns = await payrollRunService.getAll();
      setRuns(updatedRuns);
      toast.success('Payroll run locked');
    } catch (error: any) {
      toast.error(error.message || 'Failed to lock payroll run');
    }
  };

  const getStatusColor = (status: PayrollRun['status']) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'calculated': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'locked': return 'bg-purple-100 text-purple-800';
      case 'paid': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCycleName = (cycleId: string) => {
    const cycle = cycles.find(c => c.id === cycleId);
    return cycle ? cycle.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Payroll Runs</h1>
          <p className="text-sm text-neutral-600">Create and manage payroll processing runs</p>
        </div>
        <button
          onClick={() => {
            reset();
            setShowModal(true);
          }}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Payroll Run
        </button>
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Calculator className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Total Runs</p>
              <p className="text-2xl font-bold text-neutral-900">{runs.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Play className="w-8 h-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Draft Runs</p>
              <p className="text-2xl font-bold text-neutral-900">
                {runs.filter(r => r.status === 'draft').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Check className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Approved Runs</p>
              <p className="text-2xl font-bold text-neutral-900">
                {runs.filter(r => r.status === 'approved').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Lock className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Locked Runs</p>
              <p className="text-2xl font-bold text-neutral-900">
                {runs.filter(r => r.status === 'locked').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payroll Runs Table */}
      <div className="bg-white rounded-lg border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Run Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Staff Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Net
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {runs.map((run) => (
                <tr key={run.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {getCycleName(run.cycle_id)}
                    </div>
                    <div className="text-sm text-gray-500">
                      Run Date: {run.run_date}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {run.period_start} to {run.period_end}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {run.staff_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${run.total_net.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(run.status)}`}>
                      {run.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      {run.status === 'draft' && (
                        <button
                          onClick={() => handleCalculatePayroll(run.id)}
                          disabled={processingRun === run.id}
                          className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                          title="Calculate Payroll"
                        >
                          {processingRun === run.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          ) : (
                            <Calculator className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      
                      {run.status === 'calculated' && (
                        <button
                          onClick={() => handleApproveRun(run.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Approve Run"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      
                      {run.status === 'approved' && (
                        <button
                          onClick={() => handleLockRun(run.id)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Lock Run"
                        >
                          <Lock className="w-4 h-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => window.open(`/payroll/slips?runId=${run.id}`, '_blank')}
                        className="text-gray-600 hover:text-gray-900"
                        title="View Payslips"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">New Payroll Run</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pay Cycle *
                </label>
                <select
                  {...register('cycle_id')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select pay cycle</option>
                  {cycles.map(cycle => (
                    <option key={cycle.id} value={cycle.id}>
                      {cycle.name} ({cycle.frequency})
                    </option>
                  ))}
                </select>
                {errors.cycle_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.cycle_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Period Start *
                </label>
                <input
                  type="date"
                  {...register('period_start')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.period_start && (
                  <p className="text-red-500 text-sm mt-1">{errors.period_start.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Period End *
                </label>
                <input
                  type="date"
                  {...register('period_end')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.period_end && (
                  <p className="text-red-500 text-sm mt-1">{errors.period_end.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional notes about this payroll run"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Payroll Run
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
